import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Combobox } from '@headlessui/react';
import { CONFIG } from "../config";

const VehicleSearchBox = () => {
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [query, setQuery] = useState('');
  const [vehicleNumbers, setVehicleNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const navigate = useNavigate();
  const userId = parseInt(localStorage.getItem("tensor-user-id"), 10);

  // Fetch vehicle numbers
  useEffect(() => {
    const fetchVehicleNumbers = async () => {
      try {
        const response = await fetch(
          `${CONFIG.API_BASE_URL}/truck-health-vehicle-numbers/${userId}`,
          {
            headers: {
              "Content-Type": "application/json",
              "Authorization": "Bearer " + localStorage.getItem("tensor-auth-token"),
            },
          }
        );
        const data = await response.json();
        if (data.success && data.data && data.data[0]?.vehicle_numbers) {
          const numbers = data.data[0].vehicle_numbers.split(',').map(num => num.trim());
          setVehicleNumbers(numbers);
          setIsError(false);
        } else {
          setVehicleNumbers([]);
          setIsError(true);
        }
      } catch (err) {
        console.error("Failed to fetch vehicle numbers:", err);
        setIsError(true);
      }
    };

    fetchVehicleNumbers();
  }, [userId]);

  // Set initial vehicle number from .v_health_overview_text_value
  useEffect(() => {
    const span = document.querySelector('.v_health_overview_text_value');
    if (span) {
      const initialValue = span.textContent.trim();
      if (initialValue !== 'N/A' && vehicleNumbers.includes(initialValue)) {
        setSelectedVehicle(initialValue);
      }
    }
  }, [vehicleNumbers]);

  // Filter vehicle numbers based on query
  const filteredVehicles = query === ''
    ? vehicleNumbers
    : vehicleNumbers.filter(num =>
        num.toLowerCase().includes(query.toLowerCase())
      );

  const handleSelect = async (vehicle) => {
    if (!vehicle) return;
    setLoading(true);
    setIsError(false);
    try {
      const api_url = `${CONFIG.API_BASE_URL}/${CONFIG.TRUCK_HEALTH_VEHICLE_FILTERS}/${userId}?selected_filter=vehicle_number:${vehicle.trim()}`;
      const response = await fetch(api_url, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + localStorage.getItem("tensor-auth-token"),
        },
      });
      const data = await response.json();

      if (response.ok && data?.data[0]?.vehicle_id) {
        navigate(`/vehicle/${data.data[0].vehicle_id}`);
      } else {
        setIsError(true);
      }
    } catch (err) {
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full sm:w-auto flex items-center gap-2">
      <div className="relative w-full sm:w-64">
        <Combobox
          value={selectedVehicle}
          onChange={(vehicle) => {
            setSelectedVehicle(vehicle);
            setIsError(false);
            handleSelect(vehicle);
          }}
          disabled={loading}
        >
          <Combobox.Input
            className={`px-4 py-2 pr-10 border rounded w-full h-[42px] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              isError ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter vehicle number"
            onChange={(event) => setQuery(event.target.value)}
            displayValue={(vehicle) => vehicle}
          />
          <Combobox.Button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </Combobox.Button>
          <Combobox.Options className="absolute mt-1 w-full bg-white border border-gray-300 rounded shadow z-10 max-h-40 overflow-y-auto">
            {filteredVehicles.length === 0 && query !== '' ? (
              <div className="px-3 py-2 text-gray-500">No vehicles found</div>
            ) : (
              filteredVehicles.map((vehicle) => (
                <Combobox.Option
                  key={vehicle}
                  value={vehicle}
                  className={({ active }) =>
                    `px-3 py-2 cursor-pointer ${active ? 'bg-gray-100 text-blue-600' : 'text-gray-900'}`
                  }
                >
                  {vehicle}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Combobox>
      </div>
      {loading && (
        <div
          className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"
          title="Searching..."
        ></div>
      )}
      {isError && (
        <p className="text-red-500 text-sm ml-2"></p>
      )}
    </div>
  );
};

export default VehicleSearchBox;